import { DUPLICATE_COLUMN_NAMES, INVALID_IDENTIFIER, INVALID_PARAMETER_TYPE_FOR_FUNCTION, INVALID_SQL_SYNTAX, MISSING_TABLE, UNCATEGORIZED_SQL_ERROR, VALUE_NOT_RECOGNIZED } from 'app/store/__generated__/errors';
import { OPS } from 'app/lib/utils/sql';

export const translation = {
    inputs_one: 'Input Dataset',
    inputs_other: 'Input Datasets',
    operations: {
        common: {
            sourceColumn: 'Source Column',
            suggestedColumnName: 'New Column Name'
        },
        blend: {
            label: 'Join',
            helpText: 'Combine with the contents of a second dataset based on shared content in one or more columns.',
            deprecationReason: 'The Join operation is deprecated. Please use the Multi-Join operation instead.',
            emptyPreview: 'Preview Unavailable',
            emptyRightSideMessage: 'Select a dataset for the right side of the join.',
            emptyLeftSideMessage: 'Select a dataset for the left side of the join.',
            tabs: {
                preview: 'Preview',
                unmatched: 'Unmatched Rows',
                joinKeys: 'Join Keys'
            },
            unmatched: {
                unready: {
                    title: 'Join not configured',
                    message: 'Configure your join on the right in order to see which rows are unmatched.'
                },
                empty: {
                    title: 'Unmatched Rows',
                    message: 'Click calculate to see which rows from the source datasets were unmatched or dropped from the result dataset.'
                },
                loading: 'Executing query to determine which rows in the source tables are unmatched in the result set.'
            },
            joinKeys: {
                unready: {
                    title: 'Join not configured',
                    message: 'Configure your join on the right.'
                },
                empty: {
                    title: 'Join Keys',
                    message: 'Click calculate to see how the key values from one dataset match up to those in the other dataset.'
                },
                loading: 'Executing query to determine which join keys are either not matching or are matching multiple times.'
            },
            errors: {
                [VALUE_NOT_RECOGNIZED]: 'Your selected Join keys are different types. Applying this configuration may break your transformation pipeline. You can either continue, find another join key pair, or add a TRY_CAST transformation to the key columns prior to your Join.',
                [UNCATEGORIZED_SQL_ERROR]: 'Something went wrong when executing your query. It could be that the Join keys are different types. Applying this configuration may break your transformation pipeline. You can either continue, find another join key pair, or add a TRY_CAST transformation to the key columns prior to your Join.'
            }
        },
        union: {
            label: 'Union',
            helpText: 'Append the contents of this dataset to the contents of 1 or more other dataset(s) to create a new combined dataset',
            configuration: {
                brandbarTitle: 'Union Configuration',
                defaultViewName: 'Union',
                columnsCount: '{{count}} columns',
                type: {
                    'UNION ALL': { label: 'Union All', title: 'Appends each input dataset together keeping all rows' },
                    'UNION': { label: 'Union', title: 'Appends each input dataset together removing duplicate rows' },
                    'INTERSECT': {
                        label: 'Intersect',
                        title: 'Combines each dataset together keeping only the rows that exist in every dataset'
                    },
                    'EXCEPT': {
                        label: 'Except',
                        title: 'Removes rows from the first dataset that match rows from all subsequent datasets'
                    }
                },
                outputColumns: {
                    label: 'Output Columns',
                    unmappedColumnsCount: 'Unmapped Columns: {{count}} of {{total}}',
                    removeColumn: 'Remove Column',
                    removeColumnWarning: 'Removing this output column will also remove all source mappings from this Union. Are you sure?',
                    changeType: 'Change Type',
                    addSelected: 'Add Selected'
                },
                addNewDataset: {
                    title: 'Add Dataset',
                    info: "After you choose an input dataset, we'll try our best to match its columns to the current output columns.",
                    selectDatasetPlaceholder: 'Select a dataset to union…'
                },
                addNewColumn: {
                    info: 'Choose the datatype, and provide the name of the output column you would like to add.',
                    typePlaceholder: 'Type',
                    columnNamePlaceholder: 'Column name',
                    defaultValueLabel: 'Set Default',
                    buttonLabel: 'Add output column'
                }
            },
            errors: {
                [VALUE_NOT_RECOGNIZED]: 'You have matched input columns that are different types from the configured output columns. Applying this configuration may break your transformation pipeline. You can either continue, change the column matches, or add a TRY_CAST transformation to the input columns prior to your Union.',
                [UNCATEGORIZED_SQL_ERROR]: 'Something went wrong when executing your query. It could be that you have matched input columns that are different types from the configured output columns. Applying this configuration may break your transformation pipeline. You can either continue, change the column matches, or add a TRY_CAST transformation to the input columns prior to your Union.',
                [INVALID_PARAMETER_TYPE_FOR_FUNCTION]: `$t(workbench:operations.union.errors.${UNCATEGORIZED_SQL_ERROR})`
            }
        },
        extractJson: {
            label: 'Extract JSON',
            helpText: 'Extract attributes from JSON objects into new columns.',
            defaultViewName: 'Extract JSON',
            brandbarTitle: 'Extract JSON to Column',
            columnsCount: '{{count}} columns',
            schemaLoading: 'Generating the complete schema structure for the selected column',
            previewMetrics: 'Schema and attribute metrics based on preview data',
            fetchMetrics: 'Fetch Full Schema',
            source: {
                label: 'JSON Source:',
                placeholder: 'Select JSON source…'
            },
            configuration: {
                title: 'Extraction Configuration',
                hint: 'Additional extraction options will appear here based on the selected JSON element.',
                extractLabel: 'Extract object to column',
                expandLabel: 'Expand array to rows',
                outputColumnType: 'Column Value Type'
            },
            metrics: {
                name: 'Attribute Name',
                inObjectCount: 'Occurrences',
                nullValueCount: 'Null Count',
                missingFrom: 'Missing From',
                possibleTypes: 'Found Types',
                tooltip: {
                    valueInAllRows: 'This property has a value in all {{totalObjectCount}} rows',
                    valueInAllRowsWithNull: 'This property has a value in all {{inObjectCount}} rows, but {{nullValueCount}} values are null',
                    missingValueInRows: 'This property has a value in {{inObjectCount}} out of {{totalObjectCount}} rows',
                    missingValueInRowsWithNull: 'This property has a value in {{inObjectCount}} out of {{totalObjectCount}} rows, but {{nullValueCount}} values are null'
                }
            },
            errors: {
                timeout: "Schema determination timed out after {{time}} $t(common:periods.second, {'count': {{time}} }).",
                noColumnSelected: 'Select a JSON source column to begin extraction process',
                noColumnsInDataset: 'There are no columns in this dataset to extract from',
                arrayColumnWarning: 'Top level JSON arrays will be expanded into a row per array element',
                previewNotAvailable: 'Preview data not available'
            }
        },
        extract: {
            label: 'Extract',
            helpText: 'Create a new column from a fixed slice of characters in one of your existing columns.',
            form: {
                sourceColumn: {
                    placeholder: 'Choose source column'
                },
                suggestedColumnName: {
                    placeholder: 'Enter name for extracted column'
                },
                startIndex: {
                    label: 'Start Index',
                    placeholder: '# of characters to skip',
                    tooltip: 'Number of characters to skip before start of new column. Starts at 0.'
                },
                length: {
                    label: 'Length',
                    placeholder: '# of characters to include',
                    tooltip: 'Number of characters following start index value to include in new column.',
                    error: 'Must be 1 or greater. Leave blank to include everything after the Start Index.'
                }
            }
        },
        filter: {
            label: 'Filter',
            helpText: 'Filter the rows in your dataset based on their content.',
            form: {
                selectColumn: {
                    placeholder: 'Select column…'
                },
                operator: {
                    label: 'Operator',
                    placeholder: 'Select operator'
                },
                value: {
                    label: {
                        singular: 'Value',
                        plural: 'Values'
                    },
                    placeholder: 'Enter filter value',
                    tooltip: 'Values are case sensitive and do not accept wildcards or regular expressions.'
                }
            },
            conditions: {
                deleteButton: 'Delete Filter Condition'
            }
        },
        filter2: {
            label: 'Filter',
            label_create: 'Create Filter',
            label_edit: 'Edit Filter',
            helpText: 'Filter the rows in your dataset based on their content.',
            dialog: {
                configuration: 'Configuration',
                validation: 'Validation',
                condition: 'Condition',
                rowCount: 'Row Count',
                total: 'Unfiltered row count',
                operator: 'Boolean Operator',
                conditions: 'Filter Conditions'
            }
        },
        project: {
            label: 'Manage Columns',
            helpText: 'Control which columns are included in the dataset.',
            searchPlaceholder: 'Find columns…',
            emptyList: 'No matching columns'
        },
        replace: {
            label: 'Replace',
            helpText: 'Use a basic search and replace function to create a modified copy of an existing column.',
            form: {
                sourceColumn: {
                    placeholder: 'Select column'
                },
                suggestedColumnName: {
                    placeholder: 'column-name_replace'
                },
                currentValue: {
                    label: 'Current value',
                    placeholder: 'Value to replace',
                    tooltip: 'Values are case sensitive and do not accept wildcards or regular expressions. Escape spaces with a \\.'
                },
                newValue: {
                    label: 'Replacement',
                    placeholder: 'New value',
                    tooltip: 'Values are case sensitive and do not accept wildcards or regular expressions. You do not need to escape spaces here.'
                }
            }
        },
        split: {
            label: 'Split',
            helpText: 'Copy one column into two or more columns broken up by a pattern you specify.',
            form: {
                sourceColumn: {
                    placeholder: 'Enter Column…'
                },
                suggestedColumnName: {
                    placeholder: 'column-name_split'
                },
                delimiter: {
                    label: 'Delimiter',
                    placeholder: 'Value to split on',
                    tooltip: 'Values are case sensitive and do not accept wildcards. Column will be split on every occurrence of the delimiter. '
                },
                limit: {
                    label: 'Limit',
                    placeholder: '# of columns to create',
                    tooltip: 'Controls how many columns will be created. Should be ≥ the occurrences of the delimiter in your source column.'
                }
            }
        },
        formula: {
            label: 'Formula Builder',
            helpText: 'Transform your data using standard SQL functions.',
            disableText: 'Column names with “ (double quotes) are currently not supported in a formula operation. Please rename affected columns by using manage columns.'
        },
        aggregate: {
            label: 'Aggregate',
            helpText: 'Summarize your data by aggregating over one or more dimensions.'
        },
        pivot: {
            label: 'Pivot',
            helpText: 'Summarize your data by pivoting on one or more dimensions.',
            disabledText: 'Pivot is disabled because there are too many source columns.'
        },
        sql: {
            label: 'SQL',
            helpText: 'Create a new dataset using standard SQL.'
        },
        queryBuilder: {
            label: 'Query Builder',
            helpText: 'Query this dataset to understand what it contains'
        },
        tellMeMore: 'Tell me more…',
        multiJoin: {
            label: 'Multi-Join',
            helpText: 'Combine with the contents of additional datasets based on shared content in one or more columns',
            oldTitle: 'Multi-Join Configuration',
            title: 'Join Configuration',
            configuration: {
                title: 'Configuration',
                addSource: 'Add Dataset',
                addSourceHelpText: "After you choose a dataset, you'll be able to select the columns of the join keys",
                addSourcePlaceholder: 'Select dataset…',
                addKeyLabel: 'Key',
                addKeyPlaceholder: 'Select key…',
                removeSource: 'Remove dataset',
                removeCriteria: 'Remove key pair',
                output: 'Output'
            },
            output: {
                title: 'Output',
                columnCount: 'Included Columns: {{n}} of {{total}}',
                dupe: 'Duplicate column name. All column names must be unique.',
                noData: 'The current join configuration returned no data. Use the validation tab to see which rows from each source are not being included.'
            },
            validation: {
                title: 'Validation',
                noData: 'All rows in this source are included in the joined result.',
                calc: 'Calculate',
                multiplier: 'Multiplier',
                multiplicationFactor: 'Multiplication Factor',
                multiplicationFactorTooltip: 'Average number of times each row from the source table appears in the output table.',
                matched: 'Matched',
                unmatched: 'Unmatched',
                sourceKeys: 'Source Keys',
                source: 'Source',
                output: 'Output',
                rowCounts: 'Row Counts'
            },
            disconnecteCriteriadWarning: 'This join-key pair does not connect to the primary join with dataset <strong>{{source}}</strong>',
            disconnectedSourcesWarning: 'There is one or more join-keys not connected to the primary join. The columns associated with these unconnected joins will be dropped from the final output. Are you sure you want to continue?',
            missingColumnsWarning: 'The columns {{-columns, list}} have been removed from your sources. Should we remove them from your join configuration?'
        }
    },
    transformationStackLabels: {
        Filter: '$t(workbench:operations.filter.label) {{-column}}',
        Project: '$t(workbench:operations.project.label) {{-column}}',
        Replace: "$t(workbench:operations.replace.label) '{{-column}}'",
        Split: "$t(workbench:operations.split.label) '{{-column}}'",
        SubstringExtract: "$t(workbench:operations.extract.label) from '{{-column}}'",
        Blend: "$t(workbench:operations.blend.label) with '{{-column}}'",
        Formula: '$t(workbench:operations.formula.label) {{-column}}',
        Aggregation: '$t(workbench:operations.aggregate.label) {{-column}}',
        Pivot: '$t(workbench:operations.pivot.label) {{-column}}'
    },
    summarize: {
        sidebar: {
            aggregateTitle: 'Aggregate',
            pivotTitle: 'Pivot',
            searchPlaceholder: 'Find column…',
            initHint: 'Click + to add a column to your {{dimension}}',
            loadingHint: 'Loading {{dimension}}…',
            loadingColumnValues: 'Loading column values…',
            modes: {
                aggregate: 'Aggregate',
                pivot: 'Pivot'
            },
            rows: {
                aggregateSectionTitle: 'Group Bys',
                aggregateColumnSelectionTitle: 'Add Group Bys',
                pivotSectionTitle: 'Rows',
                pivotColumnSelectionTitle: 'Add Rows',
                aggregateHint: 'Click + to add a column to your Group Bys',
                pivotHint: 'Click + to add a column to your pivot table rows dimension'
            },
            columns: {
                aggregateSectionTitle: 'Columns',
                aggregateColumnSelectionTitle: 'Add Columns',
                pivotSectionTitle: 'Columns',
                pivotColumnSelectionTitle: 'Add Columns',
                aggregateHint: 'Click + to add a column to your pivot table columns dimension',
                highCardinalityColumns: 'High cardinality columns (i.e. more than 100 unique values) have been disabled.',
                pivotHint: 'Click + to add a column to your pivot table columns dimension'
            },
            measures: {
                aggregateSectionTitle: 'Measures',
                aggregateColumnSelectionTitle: 'Add Measures',
                pivotSectionTitle: 'Measures',
                pivotColumnSelectionTitle: 'Add Measures',
                aggregateHint: 'Click + to add a column to your measurements',
                pivotHint: 'Click + to add a column to your pivot table measures dimension'
            }
        }
    },
    inspectors: {
        openInSql: 'Open in SQL Editor',
        editUnion: 'Edit Union Configuration',
        editExploreTransform: 'Edit Query',
        editExtractJson: 'Edit Extract JSON Configuration',
        exploreSortWarningShort: ' Sort Order will not be propagated downstream',
        exploreSortWarning: 'Queries that contain a Sort without a Top N Limit are NOT sorted once added to the project in previews or when feeding data downstream.  A Sort with a Top N Limit will be sorted and filtered.',
        addedColumns: 'Added Columns',
        selection: {
            title: 'Selected Nodes',
            actions: {
                group: 'Create Group'
            },
            groupWarning: 'You cannot create a group if your selection contains a group node.',
            cycleWarning: 'You cannot create a group if the resulting graph contains any cycles.',
            collapsedCycleWarning: 'You cannot create a group if the resulting graph contains any cycles between groups.',
            childWarning: 'You cannot create a group if any of your selected nodes are already in a group.'
        },
        group: {
            actions: {
                expand: 'Expand',
                collapse: 'Collapse'
            }
        },
        extractJson: {
            extractedColumns: 'Extracted Columns'
        },
        formula: {
            formulas: 'Formulas',
            edit: 'Edit Formulas'
        },
        aggregate: {
            groups: 'Group Bys',
            measures: 'Measures',
            edit: 'Edit Aggregation'
        },
        pivot: {
            rows: 'Row Columns',
            pivots: 'Pivot Columns',
            edit: 'Edit Pivot'
        },
        filter: {
            edit: 'Edit Filter',
            conditions: 'Conditions'
        },
        join: {
            edit: 'Edit Join Configuration',
            keys: 'Column Mappings'
        },
        multiJoin: {
            edit: 'Edit Join Configuration'
        },
        model: {
            jobs: 'Jobs:',
            commitMessage: 'Commit Message:',
            noTagsInfo: "You haven't added any Tags yet",
            currentTag: 'Current Tag',
            activeTags: 'Active Tags ({{count}})',
            noActiveTags: 'No Active Tags',
            inSync: 'Deployed model is in sync.'
        },
        draftModel: {
            info: '<p><b>Discard</b> this draft model if the placeholder schema is no longer needed. Or <b>deploy as a model</b> so others may reuse this schema and its associated data.</p>',
            warning: 'Resolve draft model errors to enable these actions.',
            placeholder: 'Select an existing node as the data source…',
            dataSource: 'Data Source'
        }
    },
    sqlEditor: {
        title: 'SQL Editor',
        defaultName: 'New Query',
        emptyPreviewMessage: 'To see the results, enter valid SQL and press "$t(components:CodeEditor.run)".',
        columns: {
            title: 'Columns',
            hint: 'Output Columns',
            search: 'Find column…',
            columnCount_one: 'Showing 1 column',
            columnCount_other: 'Showing {{count}} columns',
            filteredColumnCount_one: 'Showing {{filteredColumn}} of 1 column',
            filteredColumnCount_other: 'Showing {{filteredColumn}} of {{count}} columns',
            empty_filtered: 'Sorry, no matching columns found.',
            empty_nocolumns: 'Run the query to see the columns.'
        },
        errors: {
            [DUPLICATE_COLUMN_NAMES]: 'The SQL query results in duplicate column name(s). Use aliases to prevent duplicate column name(s): {{ columnNames, list }}.',
            [INVALID_IDENTIFIER]: 'Unknown column "{{column}}" referenced in query.',
            [INVALID_SQL_SYNTAX]: 'Parse error on line {{errorPosition.line}} at position {{errorPosition.startPosition}}. Invalid syntax: {{- message}}',
            [MISSING_TABLE + '_matchCase']: 'Case-mismatch, try using "{{existingTableInDifferentCase}}" instead of "{{table}}".',
            [MISSING_TABLE + '_dontUseQualifiers']: 'The table "{{qualifiedTable}}" does not exist or is not accessible in Snowflake. Use "{{table}}" (without the qualifiers) to reference the non-published transformation.',
            [MISSING_TABLE + '_dontUseQuotes']: "Case-mismatch, don't use quotes here. The table {{existingTableInDifferentCase}} is all uppercase, so you don't need to quote your reference.",
            [MISSING_TABLE + '_useQuotes']: 'Case-mismatch, try using "{{existingTableInDifferentCase}}" (with quotes) to match the table. Not using quotes results in upper case references.',
            [MISSING_TABLE + '_unqualifiedWithAvailableTables']: 'Unknown table "{{qualifiedTable}}" referenced. Available tables are: {{availableTables, list}}',
            [MISSING_TABLE + '_unqualifiedWithoutAvailableTables']: 'Unknown table "{{qualifiedTable}}" referenced. Import the tables/views that you want to use into the project first or fully qualify the table with database and schema.',
            [UNCATEGORIZED_SQL_ERROR + '_withPosition']: 'Error on line {{errorPosition.line}} at position {{errorPosition.startPosition}}: {{- message}}'
        }
    },
    notebook: {
        title: 'Notebook',
        input: {
            placeholder: 'Enter message…'
        },
        newConversation: 'New Conversation',
        notConfigured: 'Notebook needs your OpenAI API key. Click <linkButton>here</linkButton> to configure it.',
        autoDescription: 'Auto-generate Description'
    },
    DataTabsPanel: {
        columnCount: "{{count}} $t(glossary:column, {'count': {{count}} })",
        columnCountDisabled: '$t(common:naDashes) $t(glossary:column_other)',
        columnCountDisabled_small: '$t(common:naDashes)',
        filteredColumnCount: "{{count}} of {{total}} $t(glossary:column, {'count': {{total}} })",
        columnCount_small: '{{count}}',
        filteredColumnCount_small: '{{count}} of {{total}}',
        filterColumns: 'Filter Columns',
        empty: {
            selection: 'Multiple nodes selected.<br/>Data Preview is unavailable.'
        },
        preview: {
            name: 'Preview',
            title: 'Quick overview of the data contained within a dataset',
            empty: 'Preview Unavailable',
            emptyMessage: 'One or more errors have impacted this dataset. Preview capabilities are not available while there are unresolved errors.',
            canceledMessage: 'Query has been canceled. Click "Last run" to refresh and try again.',
            deploying: 'Deployment in Progress',
            deployingMessage: 'Preview capabilities are not available while data is being deployed. This may take a moment depending on the amount of data.',
            unavailableSourceMessage_one: '{{- source}} does not exist or you are not authorized.',
            unavailableSourceMessage_other: '{{- source}} do not exist or you are not authorized.',
            autorunOff: 'Auto Preview Calculation is OFF',
            autorunOffMessage: '<autorun /> auto preview calculation.',
            calculateMessage: '<calculate /> now or ',
            dataPreview: 'Turn ON'
        },
        columns: {
            name: 'Columns',
            title: 'Details the structure of a dataset including column names, types and descriptions',
            empty: 'Schema Unavailable'
        },
        profile: {
            name: 'Profile',
            title: 'Compute data profiles for each column in a dataset',
            empty: 'Profile Unavailable'
        },
        sql: {
            name: 'SQL',
            title: 'Complete SQL for this transformation',
            info: 'SQL is generated from selected node and everything upstream.',
            empty: 'SQL Unavailable'
        },
        versions: {
            name: 'Versions',
            title: 'Show View History',
            noData: 'View History Unavailable.'
        },
        quality: {
            name: 'Quality',
            title: 'Show Quality Checks'
        }
    },
    ProjectTabsPanel: {
        tooltips: {
            inputs: {
                tables: 'List of tables used as inputs for this project',
                views: 'List of views used as inputs for this project'
            },
            outputs: {
                emails: 'List of emails shared from this project',
                google: 'List of Google Sheets shared from this project',
                tables: 'List of tables output from this project',
                views: 'List of views output from this project',
                models: 'List of models output from this project'
            }
        },
        empty: {
            inputs: {
                tables: 'This project does not have any table inputs yet.',
                views: 'This project does not have any view inputs yet.'
            },
            outputs: {
                emails: 'This project does not have any shared emails yet.',
                google: 'This project does not have any shared Google Sheets yet.',
                tables: 'This project does not have any table outputs yet.',
                views: 'This project does not have any view outputs yet.',
                models: 'This project does not have any model outputs yet.'
            }
        },
        inputs: {
            table: {
                name: 'Name',
                source: 'Source',
                rowCount: 'Records',
                status: 'Status',
                columnCount: 'Columns',
                projectCount: 'Used By'
            }
        },
        outputs: {
            dataDriven: 'Driven by {{fqn}}',
            api: 'Triggered by API',
            table: {
                name: 'Name',
                target: 'Target',
                trigger: 'Trigger',
                status: 'Status',
                latestRun: 'Latest Run ({{tz}})'
            }
        },
        models: {
            table: {
                currentTag: 'Current Tag'
            }
        }
    },
    GroupTabsPanel: {
        inputs: {
            name: 'Inputs',
            title: 'List of inputs for this group',
            emptyTitle: 'No Inputs',
            emptyMessage: 'Group has no inputs'
        },
        outputs: {
            name: 'Outputs',
            title: 'List of outputs for this group',
            emptyTitle: 'No Outputs',
            emptyMessage: 'Group has no outputs'
        }
    },
    GroupTableData: {
        labels: {
            name: 'Name',
            inputs: 'Source',
            outputs: 'Target'
        }
    },
    Explorer: {
        title: {
            exploration_create: 'Explore Results',
            exploration_edit: 'Edit Exploration',
            query_create: 'Build a Query',
            query_edit: 'Edit Query'
        },
        dialog: {
            button: 'Edit…',
            rowCount: '<Row Count>',
            titles: {
                filters: 'Filter',
                groups: 'Aggregate',
                sorts: 'Sort',
                summaryFilters: 'Filter Aggregates'
            },
            fields: {
                filters: {
                    column: 'Column',
                    value: 'Value',
                    operator: 'Operator',
                    trimWhitespace: 'Trim whitespace',
                    caseSensitive: 'Case sensitive',
                    isRelative: 'Relative',
                    before: 'Ago',
                    after: 'From now'
                },
                sorts: {
                    column: 'Column',
                    direction: 'Direction',
                    asc: 'Ascending',
                    desc: 'Descending',
                    limit: 'Limit (Top N)'
                },
                groups: {
                    dimensions: 'Dimensions',
                    measures: 'Measures',
                    column: 'Column',
                    bin: 'Bin',
                    function: 'Function'
                }
            },
            operators: {
                [OPS.EQUALS]: '',
                [OPS.NOT_EQUALS]: '',
                [OPS.STARTS_WITH]: '',
                [OPS.NOT_STARTS_WITH]: '',
                [OPS.CONTAINS]: '',
                [OPS.NOT_CONTAINS]: '',
                [OPS.ENDS_WITH]: '',
                [OPS.NOT_ENDS_WITH]: '',
                [OPS.IS_EMPTY]: 'is blank or null',
                [OPS.IS_NOT_EMPTY]: 'is not blank and not null',
                [OPS.IS_NULL]: '',
                [OPS.IS_NOT_NULL]: '',
                [OPS.EQ]: 'equals',
                [OPS.NOT_EQ]: 'not equals',
                [OPS.LT]: 'less than',
                [OPS.LTE]: 'less than or equal',
                [OPS.GT]: 'greater than',
                [OPS.GTE]: 'greater than or equal',
                [OPS.BEFORE]: 'A < B',
                [OPS.NOT_BEFORE]: 'A >= B',
                [OPS.AFTER]: 'A > B',
                [OPS.NOT_AFTER]: 'A <= B',
                [OPS.BETWEEN]: 'A < B and B < C',
                [OPS.NOT_BETWEEN]: 'A >= B or B >= C',
                [OPS.IN]: 'one of multiple values',
                [OPS.NOT_IN]: 'not one of multiple values',
                [OPS.ARRAYS_OVERLAP]: ''
            },
            errors: {
                typeMismatch: 'Value type must match column type',
                notNumeric: 'Must be a number'
            },
            empty: {
                filters: 'Click to add filters or find values within your dataset',
                sorts: 'Click to sort your dataset',
                groups: 'Click to summarize your dataset',
                summaryFilters: 'Click to filter your summarized dataset'
            }
        },
        empty: '<h4>You are exploring on full data!</h4><p><explore /> to sort, filter, or summarize your data.</p>',
        dimensionFilterTooltip: {
            from: 'From {{rangeFrom}}',
            to: 'To {{rangeTo}}',
            range: 'From {{rangeFrom}} to {{rangeTo}}'
        }
    }
};
